import { Trans } from "@lingui/macro";
import { Component, ErrorInfo, ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";

import Button from "components/Button/Button";

import "./AppErrorBoundary.scss";

const ROUTE_LOAD_RELOAD_KEY = "nectar:route-load-error-reload-key";

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryInnerProps = AppErrorBoundaryProps & {
  resetKey: string;
};

type AppErrorBoundaryState = {
  error: Error | null;
};

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  return String(error);
}

export function isRecoverableRouteLoadError(error: unknown) {
  const message = getErrorMessage(error);

  return [
    "ChunkLoadError",
    "Loading chunk",
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "Importing a module script failed",
    "CSS_CHUNK_LOAD_FAILED",
  ].some((text) => message.includes(text));
}

function reloadOnceForRecoverableRouteLoadError(error: unknown, resetKey: string) {
  if (typeof window === "undefined" || !isRecoverableRouteLoadError(error)) {
    return false;
  }

  const reloadKey = `${resetKey}:${getErrorMessage(error).slice(0, 160)}`;

  try {
    if (window.sessionStorage.getItem(ROUTE_LOAD_RELOAD_KEY) === reloadKey) {
      return false;
    }

    window.sessionStorage.setItem(ROUTE_LOAD_RELOAD_KEY, reloadKey);
  } catch (_error) {
    return false;
  }

  window.location.reload();
  return true;
}

class AppErrorBoundaryInner extends Component<AppErrorBoundaryInnerProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, errorInfo);
    reloadOnceForRecoverableRouteLoadError(error, this.props.resetKey);
  }

  componentDidUpdate(prevProps: AppErrorBoundaryInnerProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="AppErrorBoundary">
        <div className="AppErrorBoundary-content">
          <h1>
            <Trans>Something went wrong while loading this page.</Trans>
          </h1>
          <p>
            <Trans>Reload the app or return to the trade page to continue.</Trans>
          </p>
          <div className="AppErrorBoundary-actions">
            <Button variant="primary-action" onClick={this.handleReload}>
              <Trans>Reload</Trans>
            </Button>
            <Button variant="secondary" to="/trade">
              <Trans>Go to Trade</Trans>
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

function useRecoverableRouteLoadErrorReload(resetKey: string) {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      reloadOnceForRecoverableRouteLoadError(event.error ?? event.message, resetKey);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      reloadOnceForRecoverableRouteLoadError(event.reason, resetKey);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [resetKey]);
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  const location = useLocation();
  const resetKey = `${location.pathname}${location.search}`;

  useRecoverableRouteLoadErrorReload(resetKey);

  return <AppErrorBoundaryInner resetKey={resetKey}>{children}</AppErrorBoundaryInner>;
}
